import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/App';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Users, Film, CheckCircle, MessageCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  
  const initialRole = searchParams.get('role') || '';
  
  const [step, setStep] = useState(initialRole ? 2 : 1);
  const [role, setRole] = useState(initialRole);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleRoleSelect = (selectedRole) => {
    setRole(selectedRole);
    setStep(2);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Reset verification if phone changes
    if (e.target.name === 'phone') {
      setPhoneVerified(false);
      setVerificationSent(false);
      setVerificationCode('');
    }
  };

  const sendVerificationCode = async () => {
    if (!formData.phone || formData.phone.length < 8) {
      toast.error('Please enter a valid phone number with country code (e.g. +447700900000)');
      return;
    }
    setSendingCode(true);
    try {
      const res = await axios.post(`${API}/auth/send-phone-verification`, { phone: formData.phone });
      setVerificationSent(true);
      if (res.data.sent) {
        toast.success('Verification code sent to your WhatsApp!');
      } else {
        toast.info('WhatsApp delivery unavailable right now. You can still register — your phone will be verified later.');
        setPhoneVerified(true); // Allow registration to proceed
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setVerifying(true);
    try {
      await axios.post(`${API}/auth/verify-phone`, { phone: formData.phone, code: verificationCode });
      setPhoneVerified(true);
      toast.success('Phone number verified!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (formData.phone && !phoneVerified) {
      // Check if WhatsApp verification is available; if code was sent but not verified, block
      if (verificationSent) {
        toast.error('Please enter the verification code sent to your WhatsApp');
        return;
      }
    }

    setLoading(true);

    try {
      const user = await register({
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: role
      });
      
      toast.success('Registration successful! Your account is pending approval.');
      const dest = user.role === 'admin' ? '/admin' : user.role === 'production' ? '/production' : '/standin';
      navigate(dest, { replace: true });
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1695014192231-18462db3ebde?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxmaWxtJTIwc3R1ZGlvJTIwcHJvZHVjdGlvbiUyMHNldHxlbnwwfHx8fDE3NzIyMDM2OTd8MA&ixlib=rb-4.1.0&q=85)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#050505]" />
        
        <div className="relative z-10 flex flex-col justify-end p-16">
          <h2 className="font-['Playfair_Display'] text-4xl font-bold text-white mb-4">
            Join 2nd Team
          </h2>
          <p className="text-white/60 text-lg max-w-md">
            {role === 'production' 
              ? 'Access our network of professionally trained stand-ins for your productions.'
              : role === 'standin'
              ? 'Join our roster of elite stand-ins working on the UK\'s biggest productions.'
              : 'Choose your path and become part of the UK\'s leading casting network.'}
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Back Link */}
          {step === 1 ? (
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
              data-testid="back-to-home-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          ) : (
            <button 
              onClick={() => setStep(1)}
              className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
              data-testid="back-to-role-select"
            >
              <ArrowLeft className="w-4 h-4" />
              Change Role
            </button>
          )}

          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
              alt="2nd Team Casting" 
              className="h-36 w-auto"
            />
          </div>

          {step === 1 ? (
            // Step 1: Role Selection
            <>
              <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">
                Join 2nd Team
              </h1>
              <p className="text-white/60 mb-8">
                Select your role to continue with registration
              </p>

              <div className="space-y-4">
                <button
                  onClick={() => handleRoleSelect('production')}
                  className="w-full p-6 bg-[#121212] border border-white/10 rounded-xl text-left hover:border-[#D4AF37]/50 transition-colors group"
                  data-testid="role-production-btn"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                      <Film className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Production Team</h3>
                      <p className="text-white/60 text-sm">I'm looking to hire stand-ins for my production</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleRoleSelect('standin')}
                  className="w-full p-6 bg-[#121212] border border-white/10 rounded-xl text-left hover:border-[#D4AF37]/50 transition-colors group"
                  data-testid="role-standin-btn"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                      <Users className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Stand-In</h3>
                      <p className="text-white/60 text-sm">I want to register as a professional stand-in</p>
                    </div>
                  </div>
                </button>
              </div>
            </>
          ) : (
            // Step 2: Registration Form
            <>
              <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">
                {role === 'production' ? 'Production Registration' : 'Stand-In Registration'}
              </h1>
              <p className="text-white/60 mb-8">
                Fill in your details to create an account
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-white/80">
                    {role === 'production' ? 'Company / Your Name' : 'Full Name'}
                  </Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder={role === 'production' ? 'Production Company Ltd' : 'John Doe'}
                    required
                    className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-12"
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/80">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-12"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white/80">Phone Number (WhatsApp)</Label>
                  <div className="flex gap-2">
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+447700900000"
                      disabled={phoneVerified}
                      className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-12 flex-1"
                      data-testid="register-phone-input"
                    />
                    {!phoneVerified ? (
                      <Button
                        type="button"
                        onClick={sendVerificationCode}
                        disabled={sendingCode || !formData.phone || formData.phone.length < 8}
                        className="bg-[#25D366] hover:bg-[#25D366]/80 text-white h-12 px-4 whitespace-nowrap"
                        data-testid="send-verification-btn"
                      >
                        {sendingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-1.5" />}
                        {sendingCode ? '' : 'Verify'}
                      </Button>
                    ) : (
                      <div className="h-12 px-4 flex items-center bg-[#10B981]/10 border border-[#10B981]/30 rounded-md">
                        <CheckCircle className="w-5 h-5 text-[#10B981]" />
                      </div>
                    )}
                  </div>
                  {verificationSent && !phoneVerified && (
                    <div className="flex gap-2 mt-2">
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="6-digit code"
                        className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-10 flex-1 text-center tracking-[0.3em] font-mono"
                        data-testid="verification-code-input"
                      />
                      <Button
                        type="button"
                        onClick={verifyCode}
                        disabled={verifying || verificationCode.length !== 6}
                        className="gold-gradient text-black font-semibold h-10 px-5"
                        data-testid="verify-code-btn"
                      >
                        {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
                      </Button>
                    </div>
                  )}
                  {verificationSent && !phoneVerified && (
                    <p className="text-white/40 text-xs mt-1">Check your WhatsApp for the 6-digit code</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/80">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-12 pr-12"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-white/80">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-12"
                    data-testid="register-confirm-password-input"
                  />
                </div>

                <div className="flex items-start gap-3 p-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-white/60">
                    Your account will be reviewed by our team. You'll receive an email once approved.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full gold-gradient text-black font-bold h-12 uppercase tracking-wider hover:brightness-110 transition-all disabled:opacity-50"
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
            </>
          )}

          <p className="mt-8 text-center text-white/60">
            Already have an account?{' '}
            <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
